import React, { useState } from 'react';
import { Theme, ThemeConfig } from './AppManager';

interface ThemeUpdateRequestProps {
  onSubmit: (themeData: Theme) => void;
  onCancel: () => void;
  currentTheme?: ThemeConfig;
}

const ThemeUpdateRequest: React.FC<ThemeUpdateRequestProps> = ({
  onSubmit,
  onCancel,
  currentTheme
}) => {
  const [customTheme, setCustomTheme] = useState<Theme | null>(null);
  const [customThemeError, setCustomThemeError] = useState<string>('');
  const [customThemeFileName, setCustomThemeFileName] = useState<string>('');

  // Parse custom theme JSON
  const parseCustomTheme = (jsonText: string): Theme | null => {
    try {
      const parsed = JSON.parse(jsonText);
      
      // Check if it's in the nested format with customTheme wrapper
      const themeData = parsed.customTheme || parsed;
      
      // Validate required fields
      const requiredFields = [
        'backgroundColor', 'textColor', 'cardColor', 'topbarBackgroundColor',
        'topbarIconColor', 'topbarBorderBottomColor', 'sidebarBackgroundColor',
        'sidebarIconColor', 'sidebarIconColorSelected', 'sidebarTextColor',
        'sidebarBorderColor', 'borderColor', 'categoryIndicatorColor',
        'cartBackgroundColor', 'cartCardColor', 'cartTextColor', 'cartButtonColor',
        'cartButtonTextColor', 'cartSummaryBorder', 'payButtonColor',
        'payButtonTextColor', 'loaderColor'
      ];
      
      for (const field of requiredFields) {
        if (!themeData[field]) {
          throw new Error(`Missing required field: ${field}`);
        }
      }
      
      return themeData as Theme;
    } catch (error) {
      return null;
    }
  };

  // Handle custom theme file upload
  const handleCustomThemeUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    processThemeFile(file);
  };

  const processThemeFile = (file: File) => {
    setCustomThemeError('');
    setCustomThemeFileName(file.name);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      const parsed = parseCustomTheme(content);
      
      if (parsed) {
        setCustomTheme(parsed);
        setCustomThemeError('');
      } else {
        setCustomTheme(null);
        setCustomThemeError('Invalid theme JSON format. Please check the structure and try again.');
      }
    };
    
    reader.readAsText(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };
  
  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };
  
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };
  
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type === 'application/json' || file.name.endsWith('.json')) {
        processThemeFile(file);
      } else {
        setCustomThemeError('Please upload a JSON file.');
      }
    }
  };

  // Handle custom theme removal
  const handleRemoveCustomTheme = () => {
    setCustomTheme(null);
    setCustomThemeError('');
    setCustomThemeFileName('');
  };

  // Handle form submission
  const handleSubmit = () => {
    if (!customTheme) {
      setCustomThemeError('Please upload a valid theme JSON file.');
      return;
    }

    onSubmit(customTheme);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-container theme-update-modal">
        <div className="modal-content">
          <div className="modal-header">
            <h3>Request Theme Update</h3>
            <button 
              type="button" 
              className="modal-close"
              onClick={onCancel}
            >
              ×
            </button>
          </div>
          
          <div className="modal-body">
            <div className="current-theme-info">
              <h4>Current Theme</h4>
              <div className="theme-summary">
                <div className="theme-variant">
                  <label>Variant:</label>
                  <span>{currentTheme?.variant === 'modern-light' ? 'Modern Light' : 'Modern Dark'}</span>
                </div>
                <div className="theme-color">
                  <label>Accent Color:</label>
                  <div className="color-display">
                    <div 
                      className="color-swatch"
                      style={{ backgroundColor: currentTheme?.accentColor }}
                    ></div>
                    <span>{currentTheme?.accentColor}</span>
                  </div>
                </div>
                {currentTheme?.custom && (
                  <div className="custom-theme-indicator">
                    <label>Custom Theme:</label>
                    <span className="custom-badge">Active</span>
                  </div>
                )}
              </div>
            </div>

            <div className="theme-update-form">
              <h4>Upload New Theme</h4>
              <div className="custom-theme-info">
                <div className="info-box">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '8px', flexShrink: 0}}>
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M12 16v-4"/>
                    <path d="M12 8h.01"/>
                  </svg>
                  <div>
                    <p><strong>Need to create a custom theme?</strong></p>
                    <p>Visit <a href="https://eateroo.online/theme-customizer" target="_blank" rel="noopener noreferrer" className="theme-link">eateroo.online/theme-customizer</a> to create and download a custom theme JSON file.</p>
                  </div>
                </div>
              </div>

              {!customTheme ? (
                <div className="file-upload-section">
                  <label className="file-upload-label">
                    <input
                      type="file"
                      accept=".json"
                      onChange={handleCustomThemeUpload}
                      className="file-upload-input"
                    />
                    <div 
                      className="file-upload-area"
                      onDragOver={handleDragOver}
                      onDragEnter={handleDragEnter}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="17 8 12 3 7 8"/>
                        <line x1="12" y1="3" x2="12" y2="15"/>
                      </svg>
                      <p>Click to upload theme JSON file</p>
                      <small>or drag and drop your theme.json file here</small>
                    </div>
                  </label>

                  {customThemeError && (
                    <div className="config-error">
                      <span className="error-icon">❌</span>
                      {customThemeError}
                    </div>
                  )}
                </div>
              ) : (
                <div className="custom-theme-loaded">
                  <div className="theme-file-info">
                    <div className="file-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                        <polyline points="14,2 14,8 20,8"/>
                        <line x1="16" y1="13" x2="8" y2="13"/>
                        <line x1="16" y1="17" x2="8" y2="17"/>
                        <polyline points="10,9 9,9 8,9"/>
                      </svg>
                    </div>
                    <div className="file-details">
                      <p className="file-name">{customThemeFileName}</p>
                      <p className="file-status">Custom theme loaded successfully</p>
                    </div>
                    <button
                      type="button"
                      className="remove-theme-btn"
                      onClick={handleRemoveCustomTheme}
                      title="Remove custom theme"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"/>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                        <line x1="10" y1="11" x2="10" y2="17"/>
                        <line x1="14" y1="11" x2="14" y2="17"/>
                      </svg>
                    </button>
                  </div>

                  <div className="theme-preview-grid">
                    <div className="preview-item">
                      <span className="preview-label">Background:</span>
                      <div className="color-swatch" style={{ backgroundColor: customTheme.backgroundColor }}></div>
                      <span className="color-value">{customTheme.backgroundColor}</span>
                    </div>
                    <div className="preview-item">
                      <span className="preview-label">Text:</span>
                      <div className="color-swatch" style={{ backgroundColor: customTheme.textColor }}></div>
                      <span className="color-value">{customTheme.textColor}</span>
                    </div>
                    <div className="preview-item">
                      <span className="preview-label">Card:</span>
                      <div className="color-swatch" style={{ backgroundColor: customTheme.cardColor }}></div>
                      <span className="color-value">{customTheme.cardColor}</span>
                    </div>
                    <div className="preview-item">
                      <span className="preview-label">Topbar:</span>
                      <div className="color-swatch" style={{ backgroundColor: customTheme.topbarBackgroundColor }}></div>
                      <span className="color-value">{customTheme.topbarBackgroundColor}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="request-note">
              <p><strong>Note:</strong> Your theme update request will be reviewed by a deployment administrator. You will be notified once the request is approved and your theme has been updated.</p>
            </div>
          </div>
          
          <div className="modal-footer">
            <button
              type="button"
              className="submit-request-btn"
              onClick={handleSubmit}
              disabled={!customTheme}
            >
              Submit Request
            </button>
            <button
              type="button"
              className="cancel-btn"
              onClick={onCancel}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThemeUpdateRequest;